import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { errorText, Select, useToast } from '@/ui'

/** The last cell of a row in the apps table: the ASO app this telemetry
 *  belongs to, or a way to say which one it is.
 *
 *  Telemetry is joined to the ASO portfolio on bundle id, which fails whenever
 *  the two differ — Coda reports its Android applicationId
 *  (`com.pentarab.codaapp`) while its App Store row is `com.pentarab.coda` —
 *  and whenever the product has no App Store row yet. Those rows lose their
 *  name, their icon and the link to their ASO page.
 *
 *  The picker writes a mapping row rather than editing the bundle id, because
 *  that column is refreshed from ASC and would overwrite the correction on the
 *  next import. */
export function LinkAppCell({ appId, linked }: { appId: string; linked?: { id: number; name: string } | null }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [editing, setEditing] = useState(false)

  // Fetched only once the picker opens: most rows are matched already.
  const apps = useQuery({
    queryKey: ['aso-apps'],
    queryFn: () => api.listApps(),
    enabled: editing,
    staleTime: 5 * 60_000,
  })

  const save = useMutation({
    mutationFn: (asoAppId: number) => api.analyticsLinkApp(appId, asoAppId),
    onSuccess: (_r, asoAppId) => {
      setEditing(false)
      const name = apps.data?.items.find((a) => a.id === asoAppId)?.name
      toast.success(name ? `${appId}를 ${name}에 연결했다` : '연결했다')
      // Every analytics view resolves the same link, so they all go stale
      // together rather than disagreeing about what this app is called.
      qc.invalidateQueries({ queryKey: ['analytics-apps'] })
      qc.invalidateQueries({ queryKey: ['analytics-app'] })
      qc.invalidateQueries({ queryKey: ['analytics-breakdown'] })
    },
    onError: (e) => toast.error(errorText(e)),
  })

  if (editing) {
    return (
      <span className="flex items-center justify-end gap-2">
        <Select
          autoFocus
          aria-label={`${appId}에 연결할 ASO 앱`}
          defaultValue=""
          disabled={save.isPending}
          onChange={(e) => {
            const v = e.target.value
            if (v) save.mutate(Number(v))
          }}
          className="h-8 w-auto max-w-[12rem] text-table"
        >
          <option value="" disabled>
            {apps.isLoading ? '불러오는 중' : 'ASO 앱 고르기'}
          </option>
          {(apps.data?.items ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        <button type="button" onClick={() => setEditing(false)} className="text-meta text-muted-foreground hover:text-foreground">
          취소
        </button>
      </span>
    )
  }

  if (linked) {
    return (
      <span className="flex items-center justify-end gap-3 text-meta">
        <Link to={`/apps/${linked.id}/keywords`} className="text-accent hover:underline">
          ASO 앱
        </Link>
        <button
          type="button"
          title="이 사용량을 다른 ASO 앱에 연결한다"
          onClick={() => setEditing(true)}
          className="text-muted-foreground hover:text-foreground"
        >
          바꾸기
        </button>
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title={`번들 ID가 ${appId}인 ASO 앱이 없다. 눌러서 어느 앱인지 고른다.`}
      className="text-meta text-muted-foreground underline decoration-dotted underline-offset-4 hover:text-foreground"
    >
      {save.isPending ? '연결 중' : '연결 안 됨'}
    </button>
  )
}
